/**
 * Admin › Automations — DESIGN.md §14, §13.1.
 *
 * Three things this screen owes the team:
 *  1. **A rule you can read.** Trigger, conditions and actions in English,
 *     using the shared vocabulary (`@slate/shared`'s `describeTrigger` /
 *     `describeAction`) so the list and the dry run describe a rule the same
 *     way.
 *  2. **Failures that surface** — §14.3: "rule failures log + surface in
 *     Admin › Automations ('last run' status)". That is the M5 acceptance
 *     criterion, so the last run is a column, not a detail view.
 *  3. **A dry run before it fires** — §14.3's `POST /automations/:id/test`,
 *     against a real card by number, changing nothing.
 */
import { useState } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { CircleCheck, CircleX, FlaskConical, Pencil, Plus, Trash2, Zap } from 'lucide-react';
import {
  describeAction,
  describeCondition,
  describeTrigger,
  type AutomationNames,
  type AutomationRule,
  type AutomationTestResult,
} from '@slate/shared';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { EmptyState } from '@/components/ui/empty-state';
import { LoadingRegion } from '@/components/ui/spinner';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { AutomationForm, type AutomationDraft } from '@/components/admin/automation-form';
import { useAutomationMutations, useAutomations } from '@/features/automations/api';
import { useLookups, useUserRefs } from '@/features/lookups/api';
import { useGroups } from '@/features/admin/users-api';
import { useProjects } from '@/features/projects/api';
import { useTaskByNumber } from '@/features/tasks/api';
import { relativeTime } from '@/lib/utils';

export const Route = createFileRoute('/admin/automations')({
  component: AutomationsPage,
});

function AutomationsPage(): JSX.Element {
  const rules = useAutomations();
  const mutations = useAutomationMutations();
  const names = useAutomationNames();

  const [editing, setEditing] = useState<{ rule: AutomationRule | null } | null>(null);
  const [testing, setTesting] = useState<AutomationRule | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<AutomationRule | null>(null);

  const save = (draft: AutomationDraft): void => {
    const target = editing?.rule;
    if (target) {
      mutations.update.mutate(
        {
          id: target.id,
          name: draft.name,
          trigger: draft.trigger,
          conditions: draft.conditions,
          actions: draft.actions,
          enabled: draft.enabled,
        },
        { onSuccess: () => setEditing(null) },
      );
      return;
    }
    mutations.create.mutate(
      {
        name: draft.name,
        projectId: draft.projectId,
        trigger: draft.trigger,
        conditions: draft.conditions,
        actions: draft.actions,
        enabled: draft.enabled,
      },
      { onSuccess: () => setEditing(null) },
    );
  };

  if (rules.isLoading) return <LoadingRegion label="Loading automations" />;

  const items = rules.data ?? [];

  return (
    <div className="space-y-4">
      <div className="flex items-start justify-between gap-3">
        <p className="text-muted-foreground max-w-2xl text-sm">
          Rules run in the worker after the change is committed, and every action they take is
          audited as the rule itself — “Automation · {'{rule name}'}” in the card feed (§14.3). A
          rule never reacts to its own work beyond three levels deep.
        </p>
        <Button onClick={() => setEditing({ rule: null })}>
          <Plus />
          New rule
        </Button>
      </div>

      {items.length === 0 ? (
        <EmptyState
          icon={<Zap className="size-8" />}
          title="No automation rules yet"
          description="Rules are how the workflow enforces itself — “when a card reaches Ready for Testing, add Support and clear the testing outcome”. Seeded examples ship disabled; make one here to see the shape."
          action={<Button onClick={() => setEditing({ rule: null })}>New rule</Button>}
        />
      ) : (
        <ul className="space-y-3">
          {items.map((rule) => (
            <li key={rule.id} className="bg-card rounded-lg border p-4">
              <div className="flex flex-wrap items-start justify-between gap-2">
                <div className="min-w-0">
                  <div className="flex flex-wrap items-center gap-2">
                    <h2 className="truncate font-medium">{rule.name}</h2>
                    <Badge variant={rule.enabled ? 'default' : 'secondary'}>
                      {rule.enabled ? 'Enabled' : 'Disabled'}
                    </Badge>
                    <Badge variant="outline">
                      {rule.projectId
                        ? (names.project?.(rule.projectId) ?? 'One project')
                        : 'Every project'}
                    </Badge>
                  </div>
                  <p className="text-muted-foreground mt-1 text-sm">
                    When {describeTrigger(rule.trigger, names)}
                    {rule.conditions.length > 0 ? (
                      <>
                        {', if '}
                        {rule.conditions
                          .map((condition) => describeCondition(condition, names))
                          .join(' and ')}
                      </>
                    ) : null}
                    .
                  </p>
                  <ul className="mt-2 space-y-0.5">
                    {rule.actions.map((action, index) => (
                      <li key={index} className="text-sm">
                        <span className="text-muted-foreground mr-1.5 text-xs tabular-nums">
                          {index + 1}.
                        </span>
                        {describeAction(action, names)}
                      </li>
                    ))}
                  </ul>
                </div>

                <div className="flex shrink-0 items-center gap-1">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => mutations.update.mutate({ id: rule.id, enabled: !rule.enabled })}
                    disabled={mutations.update.isPending}
                  >
                    {rule.enabled ? 'Disable' : 'Enable'}
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    aria-label={`Dry-run ${rule.name}`}
                    title="Dry run"
                    onClick={() => setTesting(rule)}
                  >
                    <FlaskConical />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    aria-label={`Edit ${rule.name}`}
                    onClick={() => setEditing({ rule })}
                  >
                    <Pencil />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    aria-label={`Delete ${rule.name}`}
                    onClick={() => setConfirmDelete(rule)}
                  >
                    <Trash2 />
                  </Button>
                </div>
              </div>

              {/* §14.3 — the last run, including why it failed. */}
              <LastRun rule={rule} />
            </li>
          ))}
        </ul>
      )}

      {editing ? (
        <AutomationForm
          rule={editing.rule}
          open
          onClose={() => setEditing(null)}
          onSubmit={save}
          saving={mutations.create.isPending || mutations.update.isPending}
        />
      ) : null}

      {testing ? <DryRunDialog rule={testing} onClose={() => setTesting(null)} /> : null}

      <Dialog
        open={confirmDelete !== null}
        onOpenChange={(open) => (open ? undefined : setConfirmDelete(null))}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete “{confirmDelete?.name}”?</DialogTitle>
            <DialogDescription>
              The rule stops running immediately. Everything it already did stays in the audit feed
              — deleting a rule never rewrites history (§6.10).
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setConfirmDelete(null)}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={() =>
                confirmDelete &&
                mutations.remove.mutate(confirmDelete.id, {
                  onSuccess: () => setConfirmDelete(null),
                })
              }
              disabled={mutations.remove.isPending}
            >
              Delete rule
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function LastRun({ rule }: { rule: AutomationRule }): JSX.Element {
  if (!rule.lastRunAt) {
    return <p className="text-muted-foreground mt-3 border-t pt-2 text-xs">Has not run yet.</p>;
  }
  const failed = rule.lastRunStatus === 'error' || Boolean(rule.lastRunError);
  return (
    <div className="mt-3 border-t pt-2 text-xs">
      <p className={failed ? 'text-destructive font-medium' : 'text-muted-foreground'}>
        {failed ? (
          <CircleX className="mr-1 inline size-3.5 align-[-2px]" aria-hidden="true" />
        ) : (
          <CircleCheck className="mr-1 inline size-3.5 align-[-2px]" aria-hidden="true" />
        )}
        Last run {relativeTime(rule.lastRunAt)} — {rule.lastRunStatus ?? 'ok'}
      </p>
      {rule.lastRunError ? (
        <p className="text-destructive mt-1 break-words font-mono text-[0.7rem]">
          {rule.lastRunError}
        </p>
      ) : null}
    </div>
  );
}

/** §14.3 — "dry-runs against a card id and returns what would happen". */
function DryRunDialog({
  rule,
  onClose,
}: {
  rule: AutomationRule;
  onClose: () => void;
}): JSX.Element {
  const [number, setNumber] = useState('');
  const [result, setResult] = useState<AutomationTestResult | null>(null);
  const parsed = Number(number);
  const card = useTaskByNumber(Number.isInteger(parsed) && parsed > 0 ? parsed : null);
  const { test } = useAutomationMutations();

  return (
    <Dialog open onOpenChange={(open) => (open ? undefined : onClose())}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Dry-run “{rule.name}”</DialogTitle>
          <DialogDescription>
            Nothing is changed. The card is checked against the trigger and every condition, and the
            actions are described rather than run.
          </DialogDescription>
        </DialogHeader>

        <div className="flex items-end gap-2">
          <div className="flex-1">
            <Label htmlFor="dry-run-card">Card number</Label>
            <Input
              id="dry-run-card"
              inputMode="numeric"
              placeholder="1042"
              value={number}
              onChange={(event) => {
                setNumber(event.target.value.replace(/[^\d]/g, ''));
                setResult(null);
              }}
            />
          </div>
          <Button
            onClick={() =>
              card.data &&
              test.mutate(
                { id: rule.id, taskId: card.data.id },
                { onSuccess: (value) => setResult(value) },
              )
            }
            disabled={!card.data || test.isPending}
          >
            Run
          </Button>
        </div>

        {number !== '' && card.isError ? (
          <p className="text-muted-foreground text-sm">No card #{number}.</p>
        ) : card.data ? (
          <p className="text-muted-foreground truncate text-sm">
            #{card.data.number} {card.data.title}
          </p>
        ) : null}

        {result ? (
          <div className="space-y-2 rounded-md border p-3 text-sm">
            <p className="flex items-center gap-1.5">
              {result.triggerMatches ? (
                <CircleCheck className="text-primary size-4" aria-hidden="true" />
              ) : (
                <CircleX className="text-muted-foreground size-4" aria-hidden="true" />
              )}
              Trigger {result.triggerMatches ? 'would fire' : 'would not fire'}
            </p>
            <p className="flex items-center gap-1.5">
              {result.conditionsPass ? (
                <CircleCheck className="text-primary size-4" aria-hidden="true" />
              ) : (
                <CircleX className="text-muted-foreground size-4" aria-hidden="true" />
              )}
              Conditions {result.conditionsPass ? 'pass' : 'do not pass'}
            </p>
            {result.reason ? <p className="text-muted-foreground">{result.reason}</p> : null}
            {result.wouldDo.length > 0 ? (
              <ol className="list-inside list-decimal space-y-0.5">
                {result.wouldDo.map((line, index) => (
                  <li key={index}>{line}</li>
                ))}
              </ol>
            ) : null}
          </div>
        ) : null}

        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/**
 * The id → name resolver the shared describers take. Built from the lookups
 * the app already caches, so naming a rule's parts costs no extra requests.
 */
function useAutomationNames(): AutomationNames {
  const lookups = useLookups();
  const users = useUserRefs();
  const groups = useGroups();
  const projects = useProjects('all');

  const byId = <T extends { id: string }>(
    items: T[] | undefined,
    label: (item: T) => string,
  ): ((id: string) => string | undefined) => {
    const map = new Map((items ?? []).map((item) => [item.id, label(item)]));
    return (id: string) => map.get(id);
  };

  return {
    status: byId(lookups.data?.statuses, (status) => status.name),
    tag: byId(lookups.data?.tags, (tag) => tag.name),
    field: byId(lookups.data?.fields, (field) => field.name),
    user: byId(users.data, (user) => user.name),
    group: byId(groups.data, (group) => group.name),
    project: byId(projects.data?.items, (project) => project.key),
  };
}
